/**
 * 3D convex hull (quickhull) over a small point set, plus area/volume
 * helpers. Points are flat xyz triplets. Designed for interactive use with
 * up to a few hundred points - clarity over asymptotic speed.
 */

type HullFace = {
    a: number;
    b: number;
    c: number;
    // outward plane: dot(n, p) = d for points on the face
    nx: number;
    ny: number;
    nz: number;
    d: number;
};

const sub = (p: number[], i: number, j: number) => [
    p[i * 3] - p[j * 3], p[i * 3 + 1] - p[j * 3 + 1], p[i * 3 + 2] - p[j * 3 + 2]
];

const cross = (a: number[], b: number[]) => [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0]
];

const dot = (a: number[], b: number[]) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

const makeFace = (points: number[], a: number, b: number, c: number): HullFace => {
    const n = cross(sub(points, b, a), sub(points, c, a));
    const len = Math.hypot(n[0], n[1], n[2]);
    if (len > 0) {
        n[0] /= len;
        n[1] /= len;
        n[2] /= len;
    }
    return {
        a,
        b,
        c,
        nx: n[0],
        ny: n[1],
        nz: n[2],
        d: n[0] * points[a * 3] + n[1] * points[a * 3 + 1] + n[2] * points[a * 3 + 2]
    };
};

const planeDist = (face: HullFace, points: number[], i: number) => {
    return face.nx * points[i * 3] + face.ny * points[i * 3 + 1] + face.nz * points[i * 3 + 2] - face.d;
};

/**
 * Build the convex hull of the given points. Returns null when the points
 * are degenerate in 3d (fewer than 4 points, or all coincident, collinear or
 * coplanar) - callers fall back to planar handling.
 */
const buildHull = (points: number[]): HullFace[] | null => {
    const n = points.length / 3;
    if (n < 4) {
        return null;
    }

    // epsilon relative to the point cloud extent
    let ext = 0;
    for (let axis = 0; axis < 3; axis++) {
        let min = Infinity;
        let max = -Infinity;
        for (let i = 0; i < n; i++) {
            min = Math.min(min, points[i * 3 + axis]);
            max = Math.max(max, points[i * 3 + axis]);
        }
        ext = Math.max(ext, max - min);
    }
    const eps = Math.max(ext * 1e-9, 1e-12);

    // initial extremes: two most distant points along x, then the farthest
    // from that line, then the farthest from that plane
    let i0 = 0;
    let i1 = 0;
    for (let i = 1; i < n; i++) {
        if (points[i * 3] < points[i0 * 3]) i0 = i;
        if (points[i * 3] > points[i1 * 3]) i1 = i;
    }
    if (i0 === i1) {
        // all x equal: try y
        for (let i = 1; i < n; i++) {
            if (points[i * 3 + 1] < points[i0 * 3 + 1]) i0 = i;
            if (points[i * 3 + 1] > points[i1 * 3 + 1]) i1 = i;
        }
    }

    const dir = sub(points, i1, i0);
    if (Math.hypot(dir[0], dir[1], dir[2]) < eps) {
        return null;  // all points coincident
    }

    let i2 = -1;
    let best = eps;
    for (let i = 0; i < n; i++) {
        const c = cross(dir, sub(points, i, i0));
        const dist = Math.hypot(c[0], c[1], c[2]);
        if (dist > best) {
            best = dist;
            i2 = i;
        }
    }
    if (i2 < 0) {
        return null;  // collinear
    }

    let base = makeFace(points, i0, i1, i2);
    let i3 = -1;
    best = eps;
    for (let i = 0; i < n; i++) {
        const dist = Math.abs(planeDist(base, points, i));
        if (dist > best) {
            best = dist;
            i3 = i;
        }
    }
    if (i3 < 0) {
        return null;  // coplanar
    }

    // orient the base so i3 lies behind it, then build the tetrahedron
    if (planeDist(base, points, i3) > 0) {
        base = makeFace(points, i0, i2, i1);
    }
    const faces: HullFace[] = [
        base,
        makeFace(points, base.a, i3, base.b),
        makeFace(points, base.b, i3, base.c),
        makeFace(points, base.c, i3, base.a)
    ];

    // add the remaining points one by one (incremental hull): remove faces
    // visible from the new point and stitch new faces along the horizon
    const used = new Set([i0, i1, i2, i3]);
    for (let i = 0; i < n; i++) {
        if (used.has(i)) {
            continue;
        }

        const visible = faces.filter(f => planeDist(f, points, i) > eps);
        if (visible.length === 0) {
            continue;  // inside the current hull
        }

        // horizon edges: directed edges of visible faces whose reverse edge
        // is not part of another visible face
        const visibleSet = new Set(visible);
        const edgeKeys = new Set<string>();
        for (const f of visible) {
            for (const [a, b] of [[f.a, f.b], [f.b, f.c], [f.c, f.a]]) {
                edgeKeys.add(`${a},${b}`);
            }
        }
        const horizon: [number, number][] = [];
        for (const f of visible) {
            for (const [a, b] of [[f.a, f.b], [f.b, f.c], [f.c, f.a]]) {
                if (!edgeKeys.has(`${b},${a}`)) {
                    horizon.push([a, b]);
                }
            }
        }

        for (let j = faces.length - 1; j >= 0; j--) {
            if (visibleSet.has(faces[j])) {
                faces.splice(j, 1);
            }
        }
        for (const [a, b] of horizon) {
            faces.push(makeFace(points, a, b, i));
        }
    }

    return faces;
};

const hullArea = (points: number[], faces: HullFace[]): number => {
    let area = 0;
    for (const f of faces) {
        const c = cross(sub(points, f.b, f.a), sub(points, f.c, f.a));
        area += Math.hypot(c[0], c[1], c[2]) / 2;
    }
    return area;
};

const hullVolume = (points: number[], faces: HullFace[]): number => {
    // sum of signed tetrahedra against the origin; faces are outward oriented
    let volume = 0;
    for (const f of faces) {
        const a = [points[f.a * 3], points[f.a * 3 + 1], points[f.a * 3 + 2]];
        const b = [points[f.b * 3], points[f.b * 3 + 1], points[f.b * 3 + 2]];
        const c = [points[f.c * 3], points[f.c * 3 + 1], points[f.c * 3 + 2]];
        volume += dot(a, cross(b, c)) / 6;
    }
    return Math.abs(volume);
};

// true when the point lies inside or on the hull
const insideHull = (faces: HullFace[], x: number, y: number, z: number, eps = 0): boolean => {
    for (const f of faces) {
        if (f.nx * x + f.ny * y + f.nz * z - f.d > eps) {
            return false;
        }
    }
    return true;
};

export { HullFace, buildHull, hullArea, hullVolume, insideHull };
